import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams, useLocation, useNavigate } from 'react-router-dom';
import './Verify.css';

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '';

export default function Verify() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const routeState = location.state || {};
  const [certNum, setCertNum] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const widgetRef = useRef(null);
  const captchaTokenRef = useRef('');
  const isQuick = routeState.quick === true || searchParams.get('quick') === '1';

  const renderCaptcha = useCallback(() => {
    if (widgetRef.current !== null) return;
    if (!window.grecaptcha || !RECAPTCHA_SITE_KEY) return;
    const container = document.getElementById('recaptcha-container');
    if (!container) return;
    widgetRef.current = window.grecaptcha.render('recaptcha-container', {
      sitekey: RECAPTCHA_SITE_KEY,
      callback: (token) => { captchaTokenRef.current = token; },
      'expired-callback': () => { captchaTokenRef.current = ''; },
    });
  }, []);

  useEffect(() => {
    if (isQuick || result !== null) return;
    const check = setInterval(() => {
      if (window.grecaptcha && window.grecaptcha.render) {
        clearInterval(check);
        window.grecaptcha.ready(renderCaptcha);
      }
    }, 200);
    return () => clearInterval(check);
  }, [result, isQuick, renderCaptcha]);

  useEffect(() => {
    const cert = routeState.certNum || searchParams.get('cert');
    if (cert) {
      const upper = cert.toUpperCase();
      setCertNum(upper);
      if (routeState.quick) {
        navigate('/verify', { replace: true, state: null });
        setLoading(true);
        fetch(`/api/verify/quick/${encodeURIComponent(upper)}`)
          .then((r) => r.json())
          .then((data) => { setResult(data); })
          .catch(() => setError('Network error. Please try again.'))
          .finally(() => setLoading(false));
      }
    }
  }, []); // eslint-disable-line

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setResult(null);

    if (isQuick) {
      setLoading(true);
      try {
        const res = await fetch(`/api/verify/quick/${encodeURIComponent(certNum.trim())}`);
        const data = await res.json();
        if (!res.ok) setError(data.error || 'Verification failed.');
        else setResult(data);
      } catch {
        setError('Network error. Please try again.');
      } finally {
        setLoading(false);
      }
      return;
    }

    const token = captchaTokenRef.current;
    if (!token) {
      setError('Please complete the reCAPTCHA.');
      return;
    }

    setLoading(true);
    try {
      const res = await fetch('/api/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ certNum: certNum.trim(), recaptchaToken: token }),
      });
      const data = await res.json();
      if (!res.ok) setError(data.error || 'Verification failed.');
      else setResult(data);
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
      captchaTokenRef.current = '';
      widgetRef.current = null;
    }
  };

  const reset = () => {
    setResult(null);
    setError('');
    setCertNum('');
    captchaTokenRef.current = '';
    widgetRef.current = null;
  };

  const formatDate = (d) => {
    if (!d) return '—';
    const [y, m, day] = d.slice(0, 10).split('-').map(Number);
    return new Date(y, m - 1, day).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  return (
    <div className="verify-page">
      <div className="verify-card">
        <div className="verify-header">
          <img
            src="https://www.therealautograph.com/cdn/shop/files/TRA_new_2c573f6b-0c3e-42b1-9a03-9af39028cd83.png?v=1722270759"
            alt="The Real Autograph"
            className="verify-logo"
          />
          <div className="verify-title">Authentication Certification Verification</div>
          {!result && <div className="verify-subtitle">Enter your certificate number to verify its authenticity.</div>}
        </div>

        {!result && (
          <form onSubmit={handleSubmit}>
            <label className="verify-label" htmlFor="certNum">Certificate Number</label>
            <input
              id="certNum"
              className="verify-input"
              type="text"
              maxLength={6}
              value={certNum}
              onChange={(e) => setCertNum(e.target.value.toUpperCase())}
              placeholder="e.g. AB3X7K"
              autoComplete="off"
              spellCheck={false}
            />

            {!isQuick && (
              <div className="verify-recaptcha">
                <div id="recaptcha-container" />
              </div>
            )}

            {error && <div className="verify-error">{error}</div>}

            <button type="submit" className="btn btn-primary" disabled={loading || !certNum}>
              {loading ? 'Verifying…' : 'Verify Certificate'}
            </button>
            <a href="https://www.therealautograph.com" className="btn btn-primary" rel="noopener noreferrer">
              Home
            </a>
          </form>
        )}

        {!result && (
          <div className="cert-sample">
            <div className="cert-sample-label">Sample Certificate Sticker</div>
            <img src="/cert-sample.png" alt="Sample certificate sticker" className="cert-sample-img" />
          </div>
        )}

        {loading && !result && <div className="verify-loading">Verifying…</div>}

        {result && (
          <>
            <div className={`verify-result ${result.status === 'found' ? 'found' : result.status === 'unassigned' ? 'unassigned' : 'not-found'}`}>
              {result.status === 'not_found' && (
                <>
                  <div className="result-title not-found">Certificate Not Found</div>
                  <div className="result-body not-found">
                    No certificate with number <strong>{certNum}</strong> exists in our system.
                  </div>
                </>
              )}
              {result.status === 'unassigned' && (
                <>
                  <div className="result-title unassigned">Certificate Exists — Not Yet Assigned</div>
                  <div className="result-body unassigned">
                    Certificate <strong>{result.certNum}</strong> exists but has not been assigned to an item yet.
                  </div>
                </>
              )}
              {result.status === 'found' && (
                <>
                  <div className="result-title found">Certificate Verified</div>
                  {result.cert.photoUrl && (
                    <div className="result-photo">
                      <img src={result.cert.photoUrl} alt={result.cert.item || 'Certificate item'} />
                    </div>
                  )}
                  {[
                    ['Certificate #', result.cert.certNum],
                    ['Signed By', result.cert.signedBy || '—'],
                    ['Signed Date', formatDate(result.cert.signedDate)],
                    ['Item', result.cert.item || '—'],
                    ['Location', result.cert.location || '—'],
                    ...(result.cert.notes ? [['Notes', result.cert.notes]] : []),
                  ].map(([label, value]) => (
                    <div key={label} className="result-row">
                      <span className="result-row-label">{label}</span>
                      <span className="result-row-value">{value}</span>
                    </div>
                  ))}
                </>
              )}
            </div>
            <button onClick={reset} className="btn btn-secondary">Verify Another Item</button>
            <a href="https://www.therealautograph.com" className="btn btn-secondary" rel="noopener noreferrer">Home</a>
          </>
        )}
      </div>

      <div className="verify-footer">
        © {new Date().getFullYear()} The Real Autograph. All rights reserved.
      </div>
    </div>
  );
}
